import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import HourglassTopRounded from "@mui/icons-material/HourglassTopRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import EventAvailableOutlined from "@mui/icons-material/EventAvailableOutlined";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import HandymanOutlined from "@mui/icons-material/HandymanOutlined";
import EastRounded from "@mui/icons-material/EastRounded";
import { DASH } from "../../Pages/PropertyDetailPage/dashboard/tokens";
import { DashboardTabId } from "../../Pages/PropertyDetailPage/dashboard/dashboardTabs";
import { OnboardingVariant } from "./onboardingTypes";

interface OnboardingModalProps {
  /** Which pop-up to show; null keeps it closed. */
  variant: OnboardingVariant | null;
  /** Short address line for the copy, e.g. "123 Main St". */
  address?: string;
  /** The admin's most recent rejection reason, shown in the rejected pop-up. */
  rejectionReason?: string | null;
  /** Dismiss — the trigger records the "seen"/"acked" state. */
  onClose: () => void;
  /** Approved tour: jump to a dashboard tab (also dismisses). */
  onGoToTab?: (tab: DashboardTabId) => void;
  /** Rejected: reopen the claim upload so they can re-submit. */
  onResubmit?: () => void;
}

const SUPPORT_EMAIL = "info@simplerhome.app";

/** The features the approved welcome tour walks a new owner through. */
const TOUR_STEPS: {
  tab: DashboardTabId;
  title: string;
  blurb: string;
  Icon: React.ElementType;
}[] = [
  {
    tab: "home-history",
    title: "Home History",
    blurb: "Every permit, contract and record for your home, kept in one timeline you own.",
    Icon: ReceiptLongOutlined,
  },
  {
    tab: "maintenance",
    title: "Maintenance",
    blurb: "A seasonal upkeep schedule with reminders, so nothing important slips by.",
    Icon: EventAvailableOutlined,
  },
  {
    tab: "documents",
    title: "Documents",
    blurb: "A private vault for warranties, receipts, manuals and closing paperwork.",
    Icon: FolderOutlined,
  },
  {
    tab: "find-a-pro",
    title: "Find a Trusted Pro",
    blurb: "Vetted local pros for repairs and projects — request and compare bids in a click.",
    Icon: HandymanOutlined,
  },
];

const IconChip: React.FC<{ Icon: React.ElementType; color: string; bg: string; size?: number }> = ({
  Icon,
  color,
  bg,
  size = 56,
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      borderRadius: "16px",
      bgcolor: bg,
      color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Icon sx={{ fontSize: size * 0.5 }} />
  </Box>
);

const OnboardingModal: React.FC<OnboardingModalProps> = ({
  variant,
  address,
  rejectionReason,
  onClose,
  onGoToTab,
  onResubmit,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [step, setStep] = useState(0);
  const home = address || "your home";

  // Restart the tour at the hero whenever the approved pop-up (re)opens.
  useEffect(() => {
    if (variant === "approved") setStep(0);
  }, [variant]);

  const open = Boolean(variant);

  const handleGoTo = (tab: DashboardTabId) => {
    onGoToTab?.(tab);
    onClose();
  };

  // Approved tour: step 0 is the hero, steps 1..N are features.
  const totalSteps = TOUR_STEPS.length + 1;
  const feature = step > 0 ? TOUR_STEPS[step - 1] : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="sm"
      fullWidth
      aria-labelledby="onboarding-title"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : DASH.radius,
          overflow: "hidden",
        },
      }}
      data-testid={variant ? `onboarding-modal-${variant}` : "onboarding-modal"}
    >
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: "absolute", right: 10, top: 10, color: DASH.muted, zIndex: 1 }}
        data-testid="onboarding-close"
      >
        <CloseRounded />
      </IconButton>

      {/* ---------------------------------------------------------------- PENDING */}
      {variant === "pending" && (
        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <IconChip Icon={HourglassTopRounded} color={DASH.amber} bg={DASH.amberTint} />
            <Typography id="onboarding-title" variant="h5" sx={{ fontWeight: 800, color: DASH.teal }}>
              We're verifying {home}
            </Typography>
            <Typography sx={{ color: DASH.muted, maxWidth: 420 }}>
              Thanks for claiming your home! Our team is reviewing your proof of ownership — this usually
              takes a few days, and we'll email you the moment it's approved.
            </Typography>
          </Stack>

          <Box
            sx={{
              mt: 3,
              p: 2.5,
              borderRadius: DASH.radius,
              bgcolor: DASH.tealTint,
              border: `1px solid ${DASH.border}`,
            }}
          >
            <Typography sx={{ fontWeight: 700, color: DASH.teal, mb: 1.5, fontSize: 14 }}>
              What you'll unlock once you're verified
            </Typography>
            <Stack spacing={1.5}>
              {TOUR_STEPS.map(({ title, blurb, Icon }) => (
                <Stack key={title} direction="row" spacing={1.5} alignItems="flex-start">
                  <Icon sx={{ color: DASH.teal, fontSize: 20, mt: "2px", flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1d2b2b" }}>{title}</Typography>
                    <Typography sx={{ color: DASH.muted, fontSize: 13 }}>{blurb}</Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Button
            fullWidth
            variant="contained"
            onClick={onClose}
            sx={{ mt: 3, bgcolor: DASH.teal, color: "#fff", fontWeight: 800, "&:hover": { bgcolor: DASH.tealDark } }}
            data-testid="onboarding-pending-ack"
          >
            Got it
          </Button>
        </Box>
      )}

      {/* --------------------------------------------------------------- APPROVED */}
      {variant === "approved" && (
        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          {step === 0 ? (
            <Stack spacing={2.5} alignItems="center" textAlign="center">
              <IconChip Icon={CheckCircleRounded} color={DASH.green} bg={DASH.greenTint} size={64} />
              <Typography id="onboarding-title" variant="h5" sx={{ fontWeight: 800, color: DASH.teal }}>
                {home} is verified! 🎉
              </Typography>
              <Typography sx={{ color: DASH.muted, maxWidth: 440 }}>
                Welcome to your home's command center. Here's a 30-second tour of what you can do now.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 1, width: "100%" }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setStep(1)}
                  sx={{ bgcolor: DASH.teal, color: "#fff", fontWeight: 800, "&:hover": { bgcolor: DASH.tealDark } }}
                  data-testid="onboarding-tour-start"
                >
                  Take the tour
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={onClose}
                  sx={{ color: DASH.muted, fontWeight: 700 }}
                  data-testid="onboarding-tour-skip"
                >
                  Skip for now
                </Button>
              </Stack>
            </Stack>
          ) : (
            feature && (
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <IconChip Icon={feature.Icon} color={DASH.teal} bg={DASH.tealTint2} />
                  <Box>
                    <Typography sx={{ color: DASH.muted, fontSize: 12, fontWeight: 700, letterSpacing: 0.4 }}>
                      {`STEP ${step} OF ${TOUR_STEPS.length}`}
                    </Typography>
                    <Typography id="onboarding-title" variant="h6" sx={{ fontWeight: 800, color: DASH.teal }}>
                      {feature.title}
                    </Typography>
                  </Box>
                </Stack>
                <Typography sx={{ color: "#1d2b2b", fontSize: 15 }}>{feature.blurb}</Typography>
                <Button
                  variant="outlined"
                  endIcon={<EastRounded />}
                  onClick={() => handleGoTo(feature.tab)}
                  sx={{
                    alignSelf: "flex-start",
                    color: DASH.teal,
                    borderColor: DASH.border,
                    fontWeight: 700,
                    "&:hover": { borderColor: DASH.teal, bgcolor: DASH.tealTint },
                  }}
                  data-testid={`onboarding-tour-open-${feature.tab}`}
                >
                  Open {feature.title}
                </Button>
              </Stack>
            )
          )}

          {/* Progress dots + nav (hidden on the hero) */}
          {step > 0 && (
            <>
              <Stack direction="row" spacing={0.75} justifyContent="center" sx={{ mt: 3, mb: 2 }}>
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: i === step ? 20 : 7,
                      height: 7,
                      borderRadius: 4,
                      bgcolor: i === step ? DASH.teal : DASH.border,
                      transition: "width 160ms ease",
                    }}
                  />
                ))}
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button onClick={() => setStep((s) => s - 1)} sx={{ color: DASH.muted, fontWeight: 700 }}>
                  Back
                </Button>
                {step < TOUR_STEPS.length ? (
                  <Button
                    variant="contained"
                    onClick={() => setStep((s) => s + 1)}
                    sx={{ bgcolor: DASH.teal, color: "#fff", fontWeight: 800, "&:hover": { bgcolor: DASH.tealDark } }}
                    data-testid="onboarding-tour-next"
                  >
                    Next
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={onClose}
                    sx={{ bgcolor: DASH.teal, color: "#fff", fontWeight: 800, "&:hover": { bgcolor: DASH.tealDark } }}
                    data-testid="onboarding-tour-done"
                  >
                    Explore my home
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Box>
      )}

      {/* --------------------------------------------------------------- REJECTED */}
      {variant === "rejected" && (
        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <IconChip Icon={ErrorOutlineRounded} color={DASH.amber} bg={DASH.amberTint} />
            <Typography id="onboarding-title" variant="h5" sx={{ fontWeight: 800, color: DASH.teal }}>
              We need a bit more to verify {home}
            </Typography>
          </Stack>

          <Alert severity="warning" sx={{ mt: 2.5, borderRadius: DASH.radius }} data-testid="onboarding-rejected-reason">
            {rejectionReason
              ? rejectionReason
              : "Our team couldn't verify the document you sent. It may have been unclear, incomplete, or missing the APN."}
          </Alert>

          <Typography sx={{ color: DASH.muted, mt: 2.5, textAlign: "center" }}>
            Re-send a clear proof of ownership — a deed, closing statement or property tax bill showing your name —
            and we'll take another look right away.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 3 }}>
            {onResubmit && (
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  onClose();
                  onResubmit();
                }}
                sx={{ bgcolor: DASH.teal, color: "#fff", fontWeight: 800, "&:hover": { bgcolor: DASH.tealDark } }}
                data-testid="onboarding-rejected-resubmit"
              >
                Re-submit documents
              </Button>
            )}
            <Button
              fullWidth={!onResubmit}
              variant="text"
              onClick={onClose}
              sx={{ color: DASH.muted, fontWeight: 700 }}
            >
              Close
            </Button>
          </Stack>

          <Typography sx={{ color: DASH.muted, fontSize: 12.5, textAlign: "center", mt: 2 }}>
            Questions? Email{" "}
            <Box component="a" href={`mailto:${SUPPORT_EMAIL}`} sx={{ color: DASH.teal, fontWeight: 700 }}>
              {SUPPORT_EMAIL}
            </Box>
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default OnboardingModal;
